import { ReactNode, Component, ErrorInfo } from 'react';

interface GenericErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode | (() => ReactNode);
  /** When this value changes the boundary clears a previous error and retries. */
  resetKey?: string | number;
}

interface GenericErrorBoundaryState {
  hasError: boolean;
}

class GenericErrorBoundary extends Component<
  GenericErrorBoundaryProps,
  GenericErrorBoundaryState
> {
  constructor(props: GenericErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): GenericErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  componentDidUpdate(prevProps: GenericErrorBoundaryProps): void {
    if (
      this.state.hasError &&
      prevProps.resetKey !== this.props.resetKey
    ) {
      this.setState({ hasError: false });
    }
  }

  render(): ReactNode {
    const { hasError } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      if (typeof fallback === 'function') {
        return fallback();
      }
      return fallback ?? <h2>Something went wrong.</h2>;
    }

    return children;
  }
}

export default GenericErrorBoundary;
